import { Effect } from '@babylonjs/core/Materials/effect';
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import type { Scene } from '@babylonjs/core/scene';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Engine } from '@babylonjs/core/Engines/engine';

/**
 * The "enter the quantum computer" interior: a raymarched-feeling time tunnel of
 * flying neon bubbles that the mesh world dissolves into on scene-complete.
 * A fullscreen post-process; `uMix` cross-fades from the rendered scene (0) to
 * the full tunnel (1). This is the SDF hand-off teaser; wasm can drive its
 * parameters as the interior becomes real gameplay.
 */
const FRAG = /* glsl */ `
precision highp float;
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform float time;
uniform float uMix;
uniform float tint;
uniform vec2 resolution;

float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }

void main() {
  vec3 scene = texture2D(textureSampler, vUV).rgb;

  vec2 p = vUV * 2.0 - 1.0;
  p.x *= resolution.x / max(1.0, resolution.y);
  float r = length(p) + 0.0001;
  float a = atan(p.y, p.x);

  // Flying down a tunnel: depth advances with time, bubbles stream toward us.
  vec3 col = vec3(0.0);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float depth = fract(time * 0.35 + fi * 0.17);   // 0 (far) .. 1 (near)
    float scale = mix(9.0, 0.7, depth);
    vec2 q = vec2(a * 1.6, 1.0 / r) * scale + vec2(fi * 7.3, time * 2.0);
    vec2 f = fract(q) - 0.5;
    float rnd = hash(floor(q) + fi);
    float bubble = smoothstep(0.42, 0.08, length(f) + (rnd - 0.5) * 0.25);
    float fade = depth * (1.0 - depth) * 4.0;        // brightest mid-tunnel
    vec3 c = mix(vec3(0.086, 0.957, 1.0), vec3(1.0, 0.169, 0.839), fract(rnd + tint));
    col += c * bubble * fade;
  }

  // Pull toward a bright core (the fall-in).
  col += vec3(0.086, 0.957, 1.0) * smoothstep(0.55, 0.0, r) * (0.25 + 0.2 * sin(time * 3.0));
  col = col / (1.0 + col);

  gl_FragColor = vec4(mix(scene, col, clamp(uMix, 0.0, 1.0)), 1.0);
}
`;

export interface Interior {
  setMix(v: number): void;
  setTint(v: number): void;
}

export function createInterior(scene: Scene, camera: Camera, engine: Engine): Interior {
  Effect.ShadersStore['interiorFragmentShader'] = FRAG;
  const pp = new PostProcess(
    'interior',
    'interior',
    ['time', 'uMix', 'tint', 'resolution'],
    null,
    1.0,
    camera,
  );
  let mix = 0;
  let tint = 0;
  const start = performance.now();
  pp.onApply = (effect) => {
    effect.setFloat('time', (performance.now() - start) / 1000);
    effect.setFloat('uMix', mix);
    effect.setFloat('tint', tint);
    effect.setFloat2('resolution', engine.getRenderWidth(), engine.getRenderHeight());
  };
  return {
    setMix(v) {
      mix = v;
    },
    setTint(v) {
      tint = v;
    },
  };
}
